import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import api from '../../utils/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CreateDownloadDrawer = ({ isOpen = false, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    version: '',
    slug: '',
    downloader_code: '',
    downloader_url: '',
    category_id: '',
    is_visible: 'active',
    status: 'publish',
  });
  const [tagsArray, setTagsArray] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, false] }],
      ['bold', 'italic', 'underline'],
      [{ align: ['', 'center', 'right', 'justify'] }],
      [{ color: [] }],
      ['clean'],
    ],
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/api/categories');
        setCategories(res.data);
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Failed to load categories');
      }
    };
    if (isOpen) fetchCategories();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setForm({
        name: '',
        description: '',
        version: '',
        slug: '',
        downloader_code: '',
        downloader_url: '',
        category_id: '',
        is_visible: 'active',
        status: 'publish',
      });
      setTagsArray([]);
      setTagInput('');
      setFile(null);
      setError('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDescriptionChange = (value) => {
    setForm((prev) => ({
      ...prev,
      description: value,
    }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = tagInput.trim();
      if (trimmed && !tagsArray.includes(trimmed)) {
        setTagsArray((prev) => [...prev, trimmed]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTagsArray((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !file || !form.downloader_code) {
      setError('Required fields: Name, Slug, File, and Downloader Code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'category_id' && value === '') {
          // Omit empty category_id
        } else {
          data.append(key, value);
        }
      });

      data.append('tags', tagsArray.join(','));
      if (file) data.append('file', file);

      // Log detallado
      const formDataEntries = {};
      for (let [key, value] of data.entries()) {
        formDataEntries[key] = value instanceof File ? { name: value.name, size: value.size, type: value.type } : value;
      }
      console.log('Enviando FormData:', formDataEntries);

      await api.post('/api/downloads', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000, // 30 segundos
      });

      onCreated?.();
      onClose();
      setForm({
        name: '',
        description: '',
        version: '',
        slug: '',
        downloader_code: '',
        downloader_url: '',
        category_id: '',
        is_visible: 'active',
        status: 'publish',
      });
      setTagsArray([]);
      setTagInput('');
      setFile(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating download');
      console.error('Error creating download:', err, {
        response: err.response?.data,
        status: err.response?.status,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 max-w-full flex sm:w-full sm:max-w-[800px]">
        <DialogPanel className="w-full bg-white shadow-2xl rounded-l-lg flex flex-col h-screen sm:rounded-r-lg">
          <div className="p-6 overflow-y-auto flex-1 space-y-6">
            <div>
              <DialogTitle className="text-2xl font-bold text-gray-900">Create New Download</DialogTitle>
              <p className="mt-2 text-sm text-gray-600">Add a new download to the system. Fields marked with * are required.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name *</label>
                <p className="text-xs text-gray-500 mt-1">The name identifying the download (e.g., "App v1.0").</p>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Download name"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700">Slug *</label>
                <p className="text-xs text-gray-500 mt-1">Unique identifier for the URL (e.g., "app-v1").</p>
                <input
                  id="slug"
                  type="text"
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  placeholder="Download slug"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="version" className="block text-sm font-medium text-gray-700">Version</label>
                <p className="text-xs text-gray-500 mt-1">Version of the download (e.g., "1.0.0").</p>
                <input
                  id="version"
                  type="text"
                  name="version"
                  value={form.version}
                  onChange={handleChange}
                  placeholder="Version"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">Description</label>
                <p className="text-xs text-gray-500 mt-1">Brief description of the download content.</p>
                <div className="mt-1">
                  <ReactQuill
                    value={form.description}
                    onChange={handleDescriptionChange}
                    modules={quillModules}
                    className="border border-gray-200 rounded-md shadow-sm focus-within:ring-blue-500 focus-within:border-blue-500 sm:text-sm"
                    placeholder="Download description"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="downloader_code" className="block text-sm font-medium text-gray-700">Downloader Code *</label>
                <p className="text-xs text-gray-500 mt-1">Unique code to initiate the download (e.g., "745866").</p>
                <input
                  id="downloader_code"
                  type="text"
                  name="downloader_code"
                  value={form.downloader_code}
                  onChange={handleChange}
                  placeholder="Downloader code"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="downloader_url" className="block text-sm font-medium text-gray-700">Downloader URL</label>
                <p className="text-xs text-gray-500 mt-1">URL to initiate the download (optional).</p>
                <input
                  id="downloader_url"
                  type="url"
                  name="downloader_url"
                  value={form.downloader_url}
                  onChange={handleChange}
                  placeholder="Download initiation URL"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="file" className="block text-sm font-medium text-gray-700">File *</label>
                <p className="text-xs text-gray-500 mt-1">Select the file for the download.</p>
                <input
                  id="file"
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-200 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="category_id" className="block text-sm font-medium text-gray-700">Category</label>
                <p className="text-xs text-gray-500 mt-1">Select a category to classify the download.</p>
                <select
                  id="category_id"
                  name="category_id"
                  value={form.category_id}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700">Tags</label>
                <p className="text-xs text-gray-500 mt-1">Add tags to facilitate search (press Enter).</p>
                <input
                  id="tags"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Type and press Enter"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {tagsArray.map((tag, i) => (
                    <span
                      key={i}
                      className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full flex items-center gap-2"
                    >
                      {tag}
                      <button
                        type="button"
                        className="text-blue-500 hover:text-red-500 focus:outline-none"
                        onClick={() => removeTag(tag)}
                        aria-label={`Remove tag ${tag}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status</label>
                <p className="text-xs text-gray-500 mt-1">Define the current status of the download.</p>
                <select
                  id="status"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="publish">Publish</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="is_visible" className="block text-sm font-medium text-gray-700">Download Visibility</label>
                    <p className="text-xs text-gray-500 mt-1">Toggle to make the download visible to clients.</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="is_visible"
                      name="is_visible"
                      type="checkbox"
                      checked={form.is_visible === 'active'}
                      onChange={() => setForm((prev) => ({ ...prev, is_visible: prev.is_visible === 'active' ? 'inactive' : 'active' }))}
                      className="sr-only"
                    />
                    <button
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, is_visible: prev.is_visible === 'active' ? 'inactive' : 'active' }))}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        form.is_visible === 'active' ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                      role="switch"
                      aria-checked={form.is_visible === 'active'}
                      aria-labelledby="is_visible"
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          form.is_visible === 'active' ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
            </form>
          </div>
          <div className="bg-gray-50 border-t px-6 py-4 flex justify-between gap-4 sticky bottom-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              Close
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default CreateDownloadDrawer;